import React, { CSSProperties } from "react";
import { useComponentLogger } from "../../shared/logging";

export interface BaseStopPropagationProps {
    /**
     * Optional property to add stop propagation to a specific keyboard keycode.
     * Will not apply stop propagation unless specified for keyboard.
     */
    readonly keycodes?: readonly number[];
}

export interface InlineBlockStopPropagationProps extends BaseStopPropagationProps {
    readonly display: "inline-block";
}

export interface BlockStopPropagationProps extends BaseStopPropagationProps {
    /** Whether the container element should be displayed as block or inline. Default is block element. */
    readonly display?: "block";

    /** Whether the block container element will grow to full width and height. */
    readonly grow?: boolean;
}

export type StopPropagationProps = InlineBlockStopPropagationProps | BlockStopPropagationProps;

const getContainerStyle = (props: StopPropagationProps): CSSProperties | undefined => {
    if (props.display === "inline-block") {
        return {
            display: "inline-block"
        };
    } else if (props.grow) {
        return {
            height: "100%",
            width: "100%"
        };
    }

    return undefined;
};

const stopPropagationMouse = (event: React.MouseEvent<HTMLElement>) => event.stopPropagation();

const stopPropagationKeyboard = (event: React.KeyboardEvent<HTMLElement>, keycodes: readonly number[]) => {
    if (keycodes.includes(event.keyCode)) {
        event.stopPropagation();
    }
};

export const StopPropagation: React.FC<StopPropagationProps> = ({
    children,
    keycodes = [],
    ...otherProps
}) => {
    useComponentLogger("StopPropagation");
    return (
        <div
            style={getContainerStyle(otherProps)}
            onClick={stopPropagationMouse}
            onKeyDown={e => stopPropagationKeyboard(e, keycodes)}
            role="presentation"
        >
            {children}
        </div>
    );
};
